import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppMultiSelect,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import styled from 'styled-components';
import axios from 'axios';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../components/helpers/Translator';
import useIsMounted from '../../../components/hooks/useIsMounted';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import colors from '../../../../constants/colors';
import minions from '../../../../minions/minions';
import LocationSelect from '../../../components/form/Selects/LocationSelect';
import ColorPicker from '../../../components/form/ColorPicker';
import _ from 'lodash';

export default function PendingItemListPublicationHome({ element, onClose }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const userBridge = useSelector((s) => s?.module?.[moduleBridgeHex]?.userBridge);
  const [validationSchema] = useState(
    Yup.object().shape({
      title: Yup.string().required('Introduce un nombre'),
      description: Yup.string()
        .max(500, 'Máximo 500 palabras')
        .required('Introduce una descripción'),
      issuer: Yup.string().required('Introduce un autor o institución'),
      publicationYear: Yup.string().required(
        'Introduce un año de publicación/creación/edición',
      ),
      addNaturalRegion: Yup.object().test(
        'add-natural-region',
        'Debes añadir la región natural o dejar el campo vacío',
        (v) => v?.label === undefined || v?.label === null || v?.label === '',
      ),
      addTag: Yup.object().test(
        'add-tag',
        'Debes añadir la palabra clave o dejar el campo vacío',
        (v) => v?.label === undefined || v?.label === null || v?.label === '',
      ),
    }),
  );

  const handleSave = useCallback(
    async (v) =>
      axios
        .post(
          axiosPath('api_r_user_module_documentManager_item_publication_edit'),
          minions.forms.toFormData({
            moduleBridgeHex,
            itemHex: element?.hex,
            ...v,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then(() => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('loading', true));
            onClose();
            AppNotificationAlert({
              title: 'Completado',
              message: 'La publicación ha sido enviada para validación',
              type: 'success',
              duration: 10000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, element],
  );

  const handleUpload = useCallback((setFieldValue, opt) => {
    setFieldValue('deletedFile', true);
    setFieldValue('files', [...opt]);
  }, []);

  return (
    <Formik
      initialValues={{
        title: element?.title || '',
        description: element?.description || '',
        issuer: element?.issuer || '',
        publicationLocation: element?.publicationLocation || null,
        publicationYear: element?.publicationYear || '',
        publicationDateStamp: element?.publicationDateStamp || '',
        dateStamp: element?.dateStamp || '',
        publicationType: element?.publicationType || null,
        userBridge: userBridge?.id,
        mainSubject: element?.mainSubject || null,
        secondarySubjects: element?.secondarySubjects || [],
        locations: element?.locations || [],
        naturalRegions: element?.naturalRegions?.map((nr) => nr?.value) || [],
        addNaturalRegion: { label: '' },
        addedNaturalRegions: [],
        files: [],
        deletedFile: false,
        tags: element?.tags?.map((t) => t?.value) || [],
        addTag: { label: '', color: 0 },
        addedTags: [],
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values) => handleSave(values)}
    >
      {({
        setFieldValue,
        errors,
        handleSubmit,
        values,
        isSubmitting,
        setFieldError,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-12">
              <div className="alert alert-warning mb-2">
                Favor de llenar los campos obligatorios marcados con asterisco. Para
                añadir una publicación, requerirás la información bibliográfica o de
                referencia del documento. Contarás con 15 minutos para el llenado de
                la ficha, al terminar este tiempo el sistema te sacará de manera
                automática y deberás comenzar de nuevo el registro.
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="alert alert-danger">
                <b>La publicación ha sido rechazada:</b>
                <div className="mt-1">{element?.validationRejectObservations}</div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              <AppInput
                title="Título de la publicación"
                value={values.title}
                error={errors.title}
                onChange={(v) => setFieldValue('title', v)}
                isClearable
                required
              />
              <AppTextArea
                title="Resumen del contenido (máximo 500 palabras)"
                value={values.description}
                error={errors.description}
                onChange={(v) => setFieldValue('description', v)}
                required
              />
              <AppInput
                title="Autor o institución emisora de la publicación"
                value={values.issuer}
                error={errors.issuer}
                onChange={(v) => setFieldValue('issuer', v)}
                isClearable
                required
              />
              <AppDatePicker
                title="Año de publicación / creación / edición"
                value={values.publicationYear}
                error={errors.publicationYear}
                onChange={(opt) => setFieldValue('publicationYear', opt)}
                popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                dateFormat="yyyy"
                displayDateFormat="YYYY"
                showYearPicker
                isClearable
                required
              />
              <AppSingleSelect
                title="Diario o periódico oficial donde fue publicado (cuando aplique)"
                value={values?.publicationLocation}
                options={moduleData?.inputs?.publicationLocations}
                onChange={(v) => setFieldValue('publicationLocation', v)}
              />
              <AppDatePicker
                title="Fecha de publicación (cuando sea en algún Diario Oficial)"
                value={values.publicationDateStamp}
                onChange={(opt) => setFieldValue('publicationDateStamp', opt)}
                popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                dateFormat="dd/MM/yyyy"
                showYearDropdown
                showMonthDropdown
                hasToggleType
                isClearable
              />
              <AppInput
                title="Fecha de aportación"
                value={values.dateStamp}
                isReadOnly
              />
              <AppSingleSelect
                title="Tipo de publicación"
                className="form-control"
                value={values?.publicationType}
                options={moduleData?.inputs?.publicationTypes}
                onChange={(opt) => setFieldValue('publicationType', opt)}
              />
              <AppSingleSelect
                title="Recurso aportado por"
                className="form-control"
                value={values?.userBridge}
                options={moduleData?.inputs?.userBridges}
                onChange={(opt) => setFieldValue('userBridge', opt)}
              />
            </div>
            <div className="col-lg-6">
              <AppSingleSelect
                title="Temática principal (una opción)"
                value={values?.mainSubject}
                options={moduleData?.inputs?.mainSubjects}
                onChange={(opt) => setFieldValue('mainSubject', opt)}
              />
              <AppMultiSelect
                title="Temáticas secundarias"
                value={values?.secondarySubjects}
                options={moduleData?.inputs?.secondarySubjects}
                onChange={(opt) => setFieldValue('secondarySubjects', opt)}
                hasSearchBox
                hasSelectOptions
                allowSelectAll
              />
              <LocationSelect
                value={values.locations || []}
                onChange={(opt) => setFieldValue('locations', opt)}
                parentLocationID={moduleData?.parentLocation?.value ?? 1}
              />
              <AppMultiSelect
                title="Región natural (cuando aplique)"
                options={moduleData?.inputs?.naturalRegions}
                value={values?.naturalRegions}
                onChange={(opt) => setFieldValue('naturalRegions', opt)}
                isClearable
                hasSearchBox
                hasSelectOptions
              />
              <FieldArray
                name="addedNaturalRegions"
                render={(arrayHelpers) => (
                  <>
                    {values?.addedNaturalRegions?.map((nr, i) => (
                      <AddedList key={i}>
                        <AppInput
                          title="Modificar región natural"
                          value={nr?.label}
                          onChange={(v) =>
                            setFieldValue(`addedNaturalRegions[${i}].label`, v)
                          }
                          containerClassName="input-name"
                          isClearable
                        />
                        <button
                          type="button"
                          title="Eliminar"
                          className="btn btn-sm btn-danger"
                          onClick={() => arrayHelpers.remove(i)}
                        >
                          <AppRemixIcon icon="delete-bin" />
                        </button>
                      </AddedList>
                    ))}
                    <AddElement className="mt-2">
                      <AppInput
                        title="Añadir nueva región natural"
                        value={values?.addNaturalRegion?.label}
                        error={errors?.addNaturalRegion}
                        onChange={(v) =>
                          setFieldValue('addNaturalRegion', {
                            ...values?.addNaturalRegion,
                            label: v,
                          })
                        }
                        containerClassName="input-name"
                        isClearable
                      />
                      <button
                        type="button"
                        title="Añadir"
                        className="btn btn-sm btn-success"
                        disabled={_.isEmpty(values?.addNaturalRegion?.label)}
                        onClick={() => {
                          if (values?.addNaturalRegion?.label?.length < 3) {
                            setFieldError(
                              'addNaturalRegionMinLength',
                              'Introduce un mínimo de 3 caracteres',
                            );
                            return;
                          }
                          setFieldError('addNaturalRegionMinLength', undefined);
                          setFieldError('addNaturalRegion', undefined);
                          arrayHelpers.push(values?.addNaturalRegion);
                          setFieldValue('addNaturalRegion', { label: '' });
                        }}
                      >
                        <AppRemixIcon icon="add" />
                      </button>
                    </AddElement>
                  </>
                )}
              />
              <AppFileUploader onChange={(opt) => handleUpload(setFieldValue, opt)} />
              {element?.fileName && (
                <FileList uploaded>
                  <li
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="click.delete"
                      />,
                    )}
                    onClick={() =>
                      values?.files?.length === 0
                        ? setFieldValue('deletedFile', !values?.deletedFile)
                        : undefined
                    }
                  >
                    <AppRemixIcon
                      className="pointer mr-3"
                      icon="delete-bin"
                      color={colors.danger}
                    />
                    {minions.files.fileIcon(element?.fileName, true, null, 'mr-2')}
                    <FileName isRemoved={values.deletedFile === true}>
                      {element?.fileName}
                    </FileName>
                  </li>
                </FileList>
              )}
              <FileList>
                {values?.files.map((f, i) => (
                  <li
                    key={i}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="click.delete"
                      />,
                    )}
                    onClick={() => setFieldValue('files', [])}
                  >
                    <AppRemixIcon
                      className="pointer mr-3"
                      icon="delete-bin"
                      color={colors.danger}
                    />
                    {minions.files.fileIcon(f?.name, true, null, 'mr-2')}
                    <FileName>{f?.name}</FileName>
                  </li>
                ))}
              </FileList>
              <h6>Palabras clave (ingresar mínimo tres)</h6>
              <AppMultiSelect
                title="Palabras clave (ingresar mínimo tres)"
                options={moduleData?.inputs?.tags}
                value={values?.tags}
                onChange={(opt) => setFieldValue('tags', opt)}
                isClearable
                hasSearchBox
                hasSelectOptions
              />
              <FieldArray
                name="addedTags"
                render={(arrayHelpers) => (
                  <>
                    {values?.addedTags?.map((t, i) => (
                      <AddedList key={i}>
                        <ColorPicker
                          bgColor={t?.color}
                          handleChange={(v) =>
                            setFieldValue(`addedTags[${i}].color`, v)
                          }
                        />
                        <AppInput
                          title="Modificar palabra clave"
                          value={t?.label}
                          onChange={(v) => setFieldValue(`addedTags[${i}].label`, v)}
                          containerClassName="input-name"
                          isClearable
                        />
                        <button
                          type="button"
                          title="Eliminar"
                          className="btn btn-sm btn-danger"
                          onClick={() => arrayHelpers.remove(i)}
                        >
                          <AppRemixIcon icon="delete-bin" />
                        </button>
                      </AddedList>
                    ))}
                    <AddElement className="mt-2">
                      <ColorPicker
                        bgColor={values?.addTag?.color}
                        handleChange={(v) =>
                          setFieldValue('addTag', {
                            ...values?.addTag,
                            color: v,
                          })
                        }
                      />
                      <AppInput
                        title="Añadir nueva palabra clave"
                        value={values?.addTag?.label}
                        //   error={errors?.addTag || errors?.addTagMinLength}
                        error={errors?.addTag}
                        onChange={(v) =>
                          setFieldValue('addTag', {
                            ...values?.addTag,
                            label: v,
                          })
                        }
                        containerClassName="input-name"
                        isClearable
                      />
                      <button
                        type="button"
                        title="Añadir"
                        className="btn btn-sm btn-success"
                        disabled={_.isEmpty(values?.addTag?.label)}
                        onClick={() => {
                          if (values?.addTag?.label?.length < 3) {
                            setFieldError(
                              'addTagMinLength',
                              'Introduce un mínimo de 3 caracteres',
                            );
                            return;
                          }
                          setFieldError('addTagMinLength', undefined);
                          setFieldError('addTag', undefined);
                          arrayHelpers.push(values?.addTag);
                          setFieldValue('addTag', { label: '', color: 0 });
                        }}
                      >
                        <AppRemixIcon icon="add" />
                      </button>
                    </AddElement>
                  </>
                )}
              />
            </div>
          </div>
          <AppSubmitButton
            className="btn btn-xs btn-success btn-block"
            isSubmitting={isSubmitting}
            icon="save-3"
          >
            Enviar a validación
          </AppSubmitButton>
        </form>
      )}
    </Formik>
  );
}

const FileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.uploaded ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
const FileName = styled.span`
  text-decoration: ${(props) => (props.isRemoved ? 'line-through' : 'unset')};
`;

const AddedList = styled.li`
  display: flex;
  align-items: center;
  gap: 5px;
  & > .input-name {
    flex-grow: 1;
  }
`;

const AddElement = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  & > .input-name {
    flex-grow: 1;
  }
`;
